import { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { appConfig as defaultAppConfig } from '../app-config.js';
import { Icons, InferenceFreshness, NotificationDot } from './ui-atoms.jsx';
import { RiskBar, RiskMeterSheet, riskAxisLabel } from './risk-meter.jsx';
import { MessageBubble, TypingIndicator } from './chat-and-whatif.jsx';
import { HardStopSheet, MeterDrilldownSheet } from './extensions.jsx';
import { AnnotatedComposer } from './annotated-composer.jsx';
import { applyReplacements, createSendPolicy, isDraftAssessmentForText } from './send-policy.js';
import { MirrorScreen } from './mirror.jsx';
import { Composer } from './composer.jsx';
import { PrivacyBanner } from './privacy-banner.jsx';
import {
  ReportGate,
  gateTargetStyle,
  gateBlockedStyle,
  GATE_TARGET_Z,
  useReportGate,
} from './report-gate.jsx';
import {
  ALL_FEATURES,
  EnabledFeaturesProvider,
  useEnabledFeatures,
  useIsFeatureEnabled,
} from './enabled-features.jsx';
import { useMessageJump } from './use-message-jump.js';
import { useInference } from '../inference/useInference.js';
import { LimitReachedError } from '../inference/client.js';
import { resolveClientMode } from '../inference/runtime.js';
import { createNoopTelemetry } from '../harness/telemetry.js';
import { TelemetryEvent } from '../../shared/telemetry-events.js';
import { CHAT_DATA, MIRROR_DATA } from '../fixtures/data.js';
import { D2L1_DRAFT } from '../fixtures/data-ext.js';
import { severityToRiskTier } from '../../shared/risk-tiers.js';

/**
 * Adapt fixture-shape messages (`from`, fixture-shape inferences) to
 * the §6.2 history shape that useInference owns. The legacy view
 * fields (from / risk / time) are preserved so MessageBubble can
 * consume them directly; live messages get the same fields synthesized
 * by withLegacyViewFields below.
 */
function fixtureMessagesToHistory(messages) {
  return messages
    .filter((m) => m.from !== 'me-draft')
    .map((m) => ({
      id: String(m.id),
      role: m.from,
      from: m.from,
      text: m.text,
      time: m.time,
      risk: m.risk,
      inferences: m.inferences || [],
    }));
}

// Bridge: live messages from useInference carry only the §6.2 contract
// fields ({ id, role, text, inferences }). MessageBubble reads the
// legacy view fields (from / risk / time); withLegacyViewFields below
// materialises them at the render boundary so live + seeded items
// share one shape. Risk tiers go through severityToRiskTier (shared
// with AnnotatedComposer) so the same numeric confidence renders the
// same color in F4 hotspots and the composer's at-risk annotations —
// see RISK_TIER_BANDS in shared/risk-tiers.js.
function deriveRiskFromInferences(inferences) {
  if (!inferences?.length) return 0;
  const max = Math.max(...inferences.map((i) => i.confidence ?? 0));
  return severityToRiskTier(max);
}

function withLegacyViewFields(msg) {
  if (msg.from && msg.risk !== undefined && msg.time !== undefined) return msg;
  return {
    ...msg,
    from: msg.from ?? msg.role,
    time: msg.time ?? '',
    risk: msg.risk ?? deriveRiskFromInferences(msg.inferences),
  };
}

export function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function remainingTimeMs(enrolledAt, timeLimitMs) {
  if (!(timeLimitMs > 0)) return null;
  const start = Date.parse(enrolledAt ?? '');
  if (!Number.isFinite(start)) return null;
  return Math.max(0, start + timeLimitMs - Date.now());
}

// Mid-experiment feature reveal (report arms only, configured by
// backend.study.featureRevealDelayMinutes).
// Same anchor/shape as remainingTimeMs above, but inverted: ms remaining
// until the arm's feature is allowed to appear, anchored to the durably
// persisted enrolledAt so a refresh mid-delay doesn't restart the clock.
//
// Unlike remainingTimeMs, an unparseable enrolledAt resolves to "reveal
// now" (0) rather than null: a broken timing anchor must never trap a
// participant behind a delay that can never resolve.
export function timeUntilRevealMs(enrolledAt, revealDelayMs) {
  if (!(revealDelayMs > 0)) return 0;
  const start = Date.parse(enrolledAt ?? '');
  if (!Number.isFinite(start)) return 0;
  return Math.max(0, start + revealDelayMs - Date.now());
}

export function isRevealed(enrolledAt, revealDelayMs) {
  return timeUntilRevealMs(enrolledAt, revealDelayMs) === 0;
}

// True when a delay was configured but the timing anchor is unusable, so the
// participant was fail-open revealed at t0 instead of after the delay (see
// timeUntilRevealMs). The feature_revealed event carries this so analysts can
// tell such a reveal apart from a genuine on-time one — both otherwise report
// the configured reveal_delay_ms.
export function isRevealAnchorBroken(enrolledAt, revealDelayMs) {
  return revealDelayMs > 0 && !Number.isFinite(Date.parse(enrolledAt ?? ''));
}

function ChatHeader({
  contact,
  onOpenMirror,
  countdownText = null,
  showMirrorDot = false,
  hotspotsOn = false,
  showF4Dot = false,
  onToggleHotspots,
  researcherMode = false,
  onRequestFinish,
  // Report-gate props (see AppShell's gate state): gateTarget names
  // which header control, if any, is the gate's live target ('mirror' |
  // 'hotspots' | null — null when the active gate targets something
  // outside the header, e.g. F4's tap-a-message phase). gateActive is
  // true whenever ANY gate is up, regardless of target, so Finish always
  // blocks. When gateTarget is set, the whole header is elevated above
  // the report-gate scrim (GATE_TARGET_Z) so that one control stays
  // visible + clickable; z-index is otherwise the header's historical 2.
  gateTarget = null,
  gateActive = false,
  // True when report features (Mirror/Hotspots) should render at all —
  // see reportVisible in AppShell. False between gates.
  reportVisible = false,
  // Sticky (never re-hides) latch for the F4 toggle specifically — see
  // hotspotsVisible in AppShell for why this must not reuse reportVisible.
  hotspotsVisible = false,
}) {
  const f1 = useIsFeatureEnabled('F1');
  const f4 = useIsFeatureEnabled('F4');
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '52px 12px 10px',
        background: 'var(--paper)',
        borderBottom: '1px solid var(--line)',
        position: 'relative',
        zIndex: gateTarget ? GATE_TARGET_Z : 2,
        // Pin the header so a tall flash ring on the topmost message
        // can't compress it. flex children compress by default; an
        // explicit shrink: 0 makes the header an immutable height.
        flexShrink: 0,
      }}
    >
      <button
        style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 6,
          color: 'var(--ink-soft)',
        }}
      >
        {Icons.back(20, 'currentColor')}
      </button>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 999,
          background: 'linear-gradient(135deg, oklch(82% 0.07 30), oklch(72% 0.09 70))',
          color: 'var(--paper)',
          fontWeight: 600,
          fontSize: 14,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        {contact.initials}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>{contact.name}</div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--ink-muted)', marginTop: 1 }}>
          {contact.subtitle}
        </div>
      </div>
      {countdownText && (
        <div
          className="mono"
          aria-label={`Time remaining ${countdownText}`}
          style={{
            border: '1px solid var(--line)',
            borderRadius: 999,
            background: 'var(--paper-2)',
            color: 'var(--ink-soft)',
            padding: '5px 8px',
            fontSize: 10,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {countdownText}
        </div>
      )}
      {f1 && reportVisible && (
        <button
          className={gateTarget === 'mirror' ? 'report-gate-target' : undefined}
          onClick={onOpenMirror}
          aria-label="Open Mirror dashboard"
          disabled={gateActive && gateTarget !== 'mirror'}
          style={{
            position: 'relative',
            background: 'var(--indigo-soft)',
            border: 'none',
            borderRadius: 999,
            padding: '6px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            color: 'var(--indigo)',
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            ...gateBlockedStyle(gateActive && gateTarget !== 'mirror'),
          }}
        >
          {Icons.eye(13, 'var(--indigo)')} Mirror
          {(showMirrorDot || gateTarget === 'mirror') && <NotificationDot />}
        </button>
      )}
      {/* F4 participant-facing tint toggle — the researcher's "Tint
          On/Off" FeatureLauncher row (below) made visible to the
          participant, since painting every message red at once on
          reveal is poor UX. Hidden under ?researcher=1: the launcher's
          own Seg control already covers that flow.
          hotspotsVisible (not reportVisible) gates this — see AppShell:
          it's a one-way latch that stays true forever once the toggle
          is first shown. Reusing reportVisible directly would let the
          toggle vanish the instant the participant flips it back off
          in an F4-only arm — once hotspots are on and the one-time
          detail-tap is satisfied, useReportGate's gateTickRef
          permanently resolves to null (no F1/F3 fallback), so
          reportVisible would never recover and the toggle — the only
          participant-facing way back to tinting, since the
          FeatureLauncher Seg control is gated behind researcherMode —
          would strand the participant with tint off forever. */}
      {f4 && !researcherMode && hotspotsVisible && (
        <button
          className={gateTarget === 'hotspots' ? 'report-gate-target' : undefined}
          onClick={onToggleHotspots}
          aria-label="Toggle inference hotspots"
          aria-pressed={hotspotsOn}
          disabled={gateActive && gateTarget !== 'hotspots'}
          style={{
            position: 'relative',
            background: hotspotsOn ? 'var(--indigo-soft)' : 'var(--paper-2)',
            border: hotspotsOn ? 'none' : '1px solid var(--line)',
            borderRadius: 999,
            padding: '6px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            color: hotspotsOn ? 'var(--indigo)' : 'var(--ink-soft)',
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            ...gateBlockedStyle(gateActive && gateTarget !== 'hotspots'),
          }}
        >
          {Icons.eye(13, 'currentColor')} Hotspots {hotspotsOn ? 'On' : 'Off'}
          {(showF4Dot || gateTarget === 'hotspots') && <NotificationDot />}
        </button>
      )}
      {/* Participant-facing Finish affordance — the only path to the App ->
          Qualtrics handoff (there is no auto-redirect). Hidden under
          ?researcher=1 like the F4 toggle above: the researcher preview has
          no participant identity/session to hand off. */}
      {!researcherMode && (
        <button
          onClick={onRequestFinish}
          aria-label="Finish session and continue to the survey"
          disabled={gateActive}
          style={{
            background: 'var(--paper-2)',
            border: '1px solid var(--line)',
            borderRadius: 999,
            padding: '6px 10px',
            cursor: 'pointer',
            color: 'var(--ink-soft)',
            fontFamily: 'IBM Plex Mono, monospace',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            flexShrink: 0,
            ...gateBlockedStyle(gateActive),
          }}
        >
          Finish
        </button>
      )}
    </div>
  );
}

// Bottom-sheet confirm for the explicit Finish action (App -> Qualtrics
// handoff is never automatic — see harness/ParticipantGate.jsx CompleteScreen).
// Mirrors the RiskMeterSheet/HardStopSheet chrome (.sheet-overlay + .sheet)
// but is a one-off confirm, not a persisted `sheet` state: like the F2 preview
// sheets (see F2_PREVIEW_SHEETS), it deliberately does not survive a reload.
function FinishConfirmSheet({ onCancel, onConfirm }) {
  const cancelBtnRef = useRef(null);

  useEffect(() => {
    const previouslyFocused = typeof document !== 'undefined' ? document.activeElement : null;
    cancelBtnRef.current?.focus();
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, []);

  return (
    <>
      <div className="sheet-overlay" onClick={onCancel} />
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="finish-sheet-title"
        style={{ maxHeight: '48%' }}
      >
        <div className="sheet-grabber" />
        <div style={{ padding: '18px 20px 24px' }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            Finish session
          </div>
          <div
            id="finish-sheet-title"
            style={{ fontSize: 18, fontWeight: 650, lineHeight: 1.3, marginBottom: 10 }}
          >
            End the session and continue to the survey?
          </div>
          <p
            style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--ink-soft)', lineHeight: 1.5 }}
          >
            You won&apos;t be able to return to the chat after this.
          </p>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              ref={cancelBtnRef}
              type="button"
              onClick={onCancel}
              className="btn btn-ghost"
              style={{ flex: 1 }}
            >
              Keep chatting
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="btn btn-accent"
              style={{ flex: 1 }}
            >
              Finish
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

const NOOP_TELEMETRY = createNoopTelemetry();

export function App({
  enabledFeatures = ALL_FEATURES,
  client,
  researcherAllowed = true,
  appConfig = defaultAppConfig,
  sessionToken = null,
  enrolledAt = null,
  timeLimitMs = 0,
  revealDelayMs = 0,
  reportGateDelayMs: reportGateDelayMsProp,
  reportIntervalMs: reportIntervalMsProp,
  reportRefreshIntervalMs = 60_000,
  initialHotspotsOn = true,
  initialHintTiming = 'word',
  initialF2Variant = 'soft',
  draftMode = 'provider',
  showBanner = false,
  telemetry = NOOP_TELEMETRY,
  onFinish = () => {},
}) {
  // Lift enabledFeatures into state so the researcher launcher can
  // mutate it. The prop seeds the initial value (URL/harness drive what
  // is initially permitted); the launcher F1 row then acts as a *mode
  // switch* — flipping enabledFeatures to ['F1'] so the Mirror button
  // appears in the chat header, instead of jumping straight to the
  // Mirror sheet. Mirrors the participant flow.
  //
  // `client` is a test seam: tests inject a stub InferenceClient so
  // they can assert against deterministic mirrorAggregate / meterUpdate
  // payloads. Production omits it; useInference picks the default via
  // pickInferenceClient(window.location.search) — Mock by default,
  // Http when ?mock=0.
  const [features, setFeatures] = useState(enabledFeatures);
  const [bannerVisible, setBannerVisible] = useState(showBanner);
  // Coerce revealDelayMs to a finite number. A numeric *string* ("5") from an
  // enrollment record or a ?revealMin= override would otherwise pass the raw
  // `revealDelayMs > 0` guards yet poison the timer arithmetic (start + "5" is
  // string concatenation), trapping the participant behind a delay that never
  // resolves. Anything unparseable falls open to 0 (reveal immediately).
  const revealMs = Number.isFinite(Number(revealDelayMs)) ? Number(revealDelayMs) : 0;
  // Same coercion for reportIntervalMs — guards the interval-tick effect's
  // arithmetic against a stringly-typed prop the same way revealMs does.
  const reportGateDelayMs = reportGateDelayMsProp ?? reportIntervalMsProp ?? 0;
  const reportGateDelayMsCoerced = Number.isFinite(Number(reportGateDelayMs))
    ? Number(reportGateDelayMs)
    : 0;
  const reportRefreshIntervalMsCoerced = Number.isFinite(Number(reportRefreshIntervalMs))
    ? Number(reportRefreshIntervalMs)
    : 60_000;
  // Mid-experiment reveal (report arms only — revealDelayMs is 0 for every
  // other path, so `revealed` starts true and this is a no-op there).
  // Lifted here (rather than inside AppShell) because it gates the
  // EnabledFeaturesProvider's value directly: before reveal, the context
  // sees [] regardless of `features`, so the tool renders the clean
  // baseline and useInference does no focused analysis for the hidden
  // feature. AppShell owns the actual timer (see its revealDelayMs effect)
  // and calls the setter passed down here, mirroring how setEnabledFeatures
  // above is lifted-state-with-setter-passed-down.
  const [revealed, setRevealed] = useState(() => isRevealed(enrolledAt, revealMs));
  return (
    <EnabledFeaturesProvider value={revealed ? features : []}>
      <AppShell
        setEnabledFeatures={setFeatures}
        bannerVisible={bannerVisible}
        setBannerVisible={setBannerVisible}
        client={client}
        appConfig={appConfig}
        researcherAllowed={researcherAllowed}
        sessionToken={sessionToken}
        enrolledAt={enrolledAt}
        timeLimitMs={timeLimitMs}
        revealed={revealed}
        setRevealed={setRevealed}
        revealDelayMs={revealMs}
        reportGateDelayMs={reportGateDelayMsCoerced}
        reportIntervalMs={reportGateDelayMsCoerced}
        reportRefreshIntervalMs={reportRefreshIntervalMsCoerced}
        initialHotspotsOn={initialHotspotsOn}
        initialHintTiming={initialHintTiming}
        initialF2Variant={initialF2Variant}
        draftMode={draftMode}
        telemetry={telemetry}
        onFinish={onFinish}
      />
    </EnabledFeaturesProvider>
  );
}

// Sheets that exist only as the "second leg" of a live F2 preview-on-send.
// They render against the active pendingPreview and have no meaning
// without one — see localStorage hydration in AppShell for why.
const F2_PREVIEW_SHEETS = new Set(['hardstop']);

// D2 (hint timing) boundary triggers. In 'word' mode the composer's
// at-risk hints refresh on whitespace; in 'sentence' mode they refresh
// only when the participant finishes a sentence — newline, period,
// question mark, exclamation mark — so hints surface less often than
// the word-boundary level.
const WORD_BOUNDARY_RE = /\s/;
const SENTENCE_BOUNDARY_RE = /[\n.!?]/;
const HINT_TIMING_BOUNDARY_RE = { word: WORD_BOUNDARY_RE, sentence: SENTENCE_BOUNDARY_RE };
export const DEFAULT_F2_IDLE_TRIGGER_MS = 1000;

// Vite env values arrive as strings at build time. Reject empty, non-finite,
// and non-positive values so a malformed production build cannot disable the
// word-mode idle fallback or create an invalid timer delay.
export function parsePositiveNumber(value, fallback = DEFAULT_F2_IDLE_TRIGGER_MS) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

export const F2_IDLE_TRIGGER_MS = parsePositiveNumber(import.meta.env?.VITE_F2_IDLE_TRIGGER_MS);

const NEAR_BOTTOM_PX = 80;

export function isNearBottom(
  { scrollHeight, scrollTop, clientHeight },
  thresholdPx = NEAR_BOTTOM_PX
) {
  return scrollHeight - scrollTop - clientHeight < thresholdPx;
}

// Mock-mode demo seed for the composer. The MockInferenceClient's
// scoreDraft only returns DraftSpans on an exact text match (see
// buildDraftSpans in inference/client.js), so this string is the one
// draft whose typing-time annotations and pre-send preview both
// produce live data in the demo. Sourced from D2L1_DRAFT.spans rather
// than duplicated as a literal so a researcher tweaking the fixture
// in one place doesn't break the demo seed silently.
const MOCK_DEMO_DRAFT = D2L1_DRAFT.spans.map((s) => s.t).join('');

// Mock mode is the default in development and test profiles. The selected
// appConfig can force HTTP mode for production.
// In Vitest the composer starts empty so tests that paste fresh text do not
// accumulate the demo seed in front of their input.
function isMockSeedActive(search, config = defaultAppConfig) {
  try {
    if (import.meta.env?.MODE === 'test' && !document.getElementById('root')) return false;
  } catch {
    /* import.meta.env or document unavailable in some environments */
  }
  return resolveClientMode(search ?? '', config) !== 'http';
}

function AppShell({
  setEnabledFeatures,
  bannerVisible,
  setBannerVisible,
  client,
  appConfig = defaultAppConfig,
  researcherAllowed,
  sessionToken,
  enrolledAt,
  timeLimitMs,
  revealed,
  setRevealed,
  revealDelayMs,
  reportGateDelayMs: reportGateDelayMsProp,
  reportIntervalMs: reportIntervalMsProp,
  reportRefreshIntervalMs = 60_000,
  initialHotspotsOn,
  initialHintTiming,
  initialF2Variant,
  draftMode,
  telemetry,
  onFinish = () => {},
}) {
  const reportGateDelayMs = reportGateDelayMsProp ?? reportIntervalMsProp ?? 0;
  const [sheet, setSheet] = useState(null); // 'meter' | 'mirror' | 'hardstop' | 'drilldown' | null
  // One-off confirm for the explicit Finish action — deliberately NOT part of
  // `sheet` (which persists to localStorage via privmeter-state) so a reload
  // mid-confirm doesn't resurrect the dialog.
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);
  // The feature was hidden at session start and appears mid-experiment (a
  // configured reveal delay). Everything reveal-specific — the telemetry
  // event, the analysis backfill, the notification dots — keys off this;
  // a 0-delay arm is revealed from first render and behaves as it always did.
  const revealWasDelayed = revealDelayMs > 0;
  // F4 tint default: the report f4 arm starts OFF (initialHotspotsOn=false,
  // see ParticipantGate) so reveal doesn't paint every message red at once —
  // the participant flips it on via the header Hotspots toggle. Every other
  // path (researcher demo, non-F4 arms) keeps the historical default-on.
  const [hotspotsOn, setHotspotsOn] = useState(initialHotspotsOn);
  // Reveal-notification "seen" state — cleared the first time the
  // participant opens/toggles the corresponding feature. Only rendered
  // when revealWasDelayed (see the ChatHeader/RiskBar showDot props
  // below), so an arm with the default 0-minute delay never shows a dot —
  // this key exists for every reveal-eligible feature (F1/F3/F4; F2 is a
  // whatif feature and is never delayed) so it round-trips through
  // localStorage uniformly regardless of which arm is active.
  const [entrySeen, setEntrySeen] = useState({ F1: false, F3: false, F4: false });
  // One-time "tapped a message to see its inference detail" flag for the
  // report f4 arm's report gate — distinct from entrySeen (which
  // tracks the reveal-notification dot, not the gate's first-time tap
  // requirement). Persisted alongside entrySeen below.
  const [f4DetailSeen, setF4DetailSeen] = useState(false);
  // Whether the participant has engaged with a report sheet (Mirror /
  // Risk Meter) at least once since the last explicit close. Needed
  // because jumpToMessage's onBeforeJump (below) synchronously closes
  // the sheet to reveal the flashed chat message — that's in-report
  // navigation, not the participant dismissing the report, so
  // reportVisible (see AppShell) must not drop just because `sheet`
  // transiently passed through null. Set on openMirror/expandRiskMeter,
  // cleared only by closeMirror/closeRiskMeter/closeDrilldown — an
  // actual exit, matching the "closing the sheet, not the gate, hides
  // the entry point again" contract below.
  const [reportEngaged, setReportEngaged] = useState(false);
  // Shared jump-and-flash behavior — used by F1 Mirror evidence rows
  // and F3 drilldown contributing rows. The hook owns msgRefs,
  // flashMsgId, and the expanded-tooltip state (the F4 hotspot toggle
  // also reads/writes expandedMsgId, so the hook returns its setter).
  const { msgRefs, jumpToMessage, flashMsgId, expandedMsgId, setExpandedMsgId } = useMessageJump({
    onBeforeJump: () => setSheet(null),
  });

  const f1Enabled = useIsFeatureEnabled('F1');
  const f2Enabled = useIsFeatureEnabled('F2');
  const f3Enabled = useIsFeatureEnabled('F3');
  const f4Enabled = useIsFeatureEnabled('F4');
  const enabledFeatureIds = useEnabledFeatures();

  // Researcher gate (T13): the feature launcher is visible only with ?researcher=1.
  // Read on every render so a same-page URL update flips the gate without
  // a remount. Cheap — string parse on a short URL.
  const researcherMode =
    researcherAllowed &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('researcher') === '1';
  // D2 — hint timing. The F2 composer is ALWAYS the annotated one;
  // this axis only governs when scoreDraft fires (see onDraftTextChange):
  //   'word'     → only when the participant types a word boundary
  //   'sentence' → only when the participant types a sentence boundary
  const [hintTiming, setHintTiming] = useState(initialHintTiming); // 'word' | 'sentence'
  const [typingOpenIdx, setTypingOpenIdx] = useState(1); // which span popover is open

  // D4 agency and send lifecycle are owned by Send Policy. AppShell keeps
  // only the pending value needed to render HardStopSheet.
  const [pendingPreview, setPendingPreview] = useState(null);
  // Axis the participant tapped in RiskMeterSheet's breakdown.
  // Forwarded to MeterDrilldownSheet's initialAxis on the next render
  // so the drill-down opens already focused on that axis instead of
  // the default 'Location'. Cleared on sheet close to keep "next time
  // I open Drilldown directly" defaulting to Location, matching the
  // researcher launcher's behavior.
  const [drilldownAxis, setDrilldownAxis] = useState(null);
  const openDrilldownForAxis = (axis) => {
    recordUiEvent(TelemetryEvent.F3_DRILLDOWN_OPEN, { axis: riskAxisLabel(axis) });
    setDrilldownAxis(axis);
    setSheet('drilldown');
  };
  // D4 — agency. Which F2 variant the active session is showing.
  // Researcher launcher mutates this; production builds default to
  // 'soft'. Soft sends optimistically (the in-composer Redact button
  // is the nudge); Hard defers commit behind the forced-ack sheet.
  const [f2DesignVariant, setF2DesignVariant] = useState(initialF2Variant); // 'soft' | 'hard'
  // Composer text is owned here so AppShell can clear on commit
  // and preserve on cancel ("Back" in the hard-stop sheet). One state
  // for both the plain (non-F2) and annotated (F2) composer, so a
  // draft survives the researcher toggling F-rows mid-session.
  //
  // Mock-mode seed: when running the in-browser demo against the
  // MockInferenceClient, we pre-fill the composer with MOCK_DEMO_DRAFT
  // so the participant can hit Send immediately and see live preview
  // results — the mock client only returns inference data for that
  // exact text. Empty string in test mode and live mode (?mock=0).
  const [draftText, setDraftText] = useState(() =>
    typeof window !== 'undefined' && isMockSeedActive(window.location.search, appConfig)
      ? MOCK_DEMO_DRAFT
      : ''
  );
  const draftIdleTimerRef = useRef(null);

  // The idle fallback is only part of the word-timing condition. Cleanup
  // happens both when the timing mode changes and when AppShell unmounts, so
  // an old condition can never score a draft after the participant switches
  // to sentence timing or leaves the session.
  useEffect(() => {
    return () => {
      if (draftIdleTimerRef.current !== null) {
        clearTimeout(draftIdleTimerRef.current);
        draftIdleTimerRef.current = null;
      }
    };
  }, [hintTiming]);
  const draftTextRef = useRef(draftText);
  draftTextRef.current = draftText;
  const redactInFlightRef = useRef(false);
  const draftRevisionRef = useRef(0);
  const redactAttemptSeqRef = useRef(0);
  useEffect(
    () => () => {
      draftRevisionRef.current += 1;
    },
    []
  );

  // Persist UI state across refreshes. Defensive against environments
  // where localStorage is unavailable (jsdom-without-storage, sandboxed
  // iframes) — log so corrupted state isn't silently reset every refresh.
  //
  // The F2 preview sheet (hardstop) is NOT restored on cold start: it
  // requires pendingPreview, which is session-only and never persisted.
  // Restoring would render the fixture-bridge demo data over an empty
  // live preview, and the sheet's send would silently drop the
  // participant's real message (study-data corrupting, H1).
  useEffect(() => {
    try {
      const saved = window.localStorage?.getItem('privmeter-state');
      if (saved) {
        const s = JSON.parse(saved);
        if (s.sheet && !F2_PREVIEW_SHEETS.has(s.sheet)) setSheet(s.sheet);
        if (typeof s.hotspotsOn === 'boolean') setHotspotsOn(s.hotspotsOn);
        if (s.entrySeen && typeof s.entrySeen === 'object') {
          setEntrySeen((prev) => ({ ...prev, ...s.entrySeen }));
        }
        if (typeof s.f4DetailSeen === 'boolean') setF4DetailSeen(s.f4DetailSeen);
      }
    } catch (e) {
      console.warn('[localStorage read] privmeter-state', e);
    }
  }, []);
  useEffect(() => {
    try {
      // Same exclusion on the write side: don't persist a preview sheet
      // that would re-open empty after a refresh.
      const persistedSheet = F2_PREVIEW_SHEETS.has(sheet) ? null : sheet;
      const raw = window.localStorage?.getItem('privmeter-state');
      const existing = raw ? JSON.parse(raw) : {};
      window.localStorage?.setItem(
        'privmeter-state',
        JSON.stringify({
          ...existing,
          sheet: persistedSheet,
          hotspotsOn,
          entrySeen,
          f4DetailSeen,
        })
      );
    } catch (e) {
      console.warn('[localStorage write] privmeter-state', e);
    }
  }, [sheet, hotspotsOn, entrySeen, f4DetailSeen]);

  const data = CHAT_DATA;
  const initialHistory = useMemo(() => fixtureMessagesToHistory(data.messages), [data.messages]);
  // SF1 — surface send-failures to the participant. console.error alone would
  // let a participant whose send rejected proceed thinking the tool said "no
  // risks" (a rejected send is observationally distinct from no findings).
  // The banner makes the failure visible so the session is interpretable.
  const [sendError, setSendError] = useState(null);
  const [limitReached, setLimitReached] = useState(null);
  // Send Policy reads this ref after async preview and while a hard-stop
  // sheet is open, so deadline checks always see the latest limit state.
  const limitReachedRef = useRef(limitReached);
  limitReachedRef.current = limitReached;
  const [remainingMs, setRemainingMs] = useState(() => remainingTimeMs(enrolledAt, timeLimitMs));
  const handleSendFailure = (err) => {
    if (err instanceof LimitReachedError) {
      setSendError(null);
      setLimitReached(err.limit ?? 'tokens');
      setSheet(null);
      return;
    }
    setSendError(err instanceof Error ? err.message : String(err));
  };
  const {
    send,
    previewDraft,
    scheduleReply,
    refreshAnalysis,
    history,
    latestMirror,
    latestMeterUpdate,
    draftAssessment,
    draftScoring,
    axisEvidence,
    analysisUpdating,
    analysisSettled,
    analysisErrors,
    scoreDraft,
    cancelDraftScoring,
    replyTyping,
    conversationId,
    inferenceTriggerTimes,
  } = useInference({
    initialHistory,
    client,
    appConfig,
    sessionToken,
    draftMode,
    onInferenceLog: telemetry?.recordInference,
    onDraftScore: (phase, detail) =>
      recordUiEvent(
        phase === 'requested'
          ? TelemetryEvent.DRAFT_SCORE_REQUESTED
          : TelemetryEvent.DRAFT_SCORE_SETTLED,
        detail
      ),
    onSendError: handleSendFailure,
    enabledFeatures: enabledFeatureIds,
    // When a report-gate interval is configured, background analysis
    // (F1 mirror / F3 meter / F4 findings) decouples from every send and
    // instead runs on the interval tick below (see the reportIntervalMs
    // effect) — bounding call frequency instead of firing per message.
    // reportIntervalMs is 0 for every non-report path, so this is a no-op
    // there (analyzeOnSend stays true, unchanged historical behavior).
    analyzeOnSend: !(reportGateDelayMs > 0),
    onMessageSent: ({ conversationId: sentConversationId, messageId, message }) => {
      telemetry?.recordEvent?.(
        TelemetryEvent.MESSAGE_SENT,
        { message },
        { conversationId: sentConversationId, msgId: messageId }
      );
    },
  });
  const recordUiEvent = (type, detail = {}) => {
    telemetry?.recordEvent?.(type, detail, {
      conversationId,
      ...(detail?.msgId != null ? { msgId: detail.msgId } : {}),
    });
  };
  const f1Ready = !analysisUpdating.F1 && (Boolean(analysisErrors.F1) || latestMirror !== null);

  const f3Ready =
    !analysisUpdating.F3 && (Boolean(analysisErrors.F3) || latestMeterUpdate.length > 0);

  const f4Ready =
    !analysisUpdating.F4 &&
    (Boolean(analysisErrors.F4) || analysisSettled.F4 || !history.some((m) => m.role === 'me'));

  const isPreparationReady = enabledFeatureIds.includes('F1')
    ? f1Ready
    : enabledFeatureIds.includes('F3')
      ? f3Ready
      : enabledFeatureIds.includes('F4')
        ? f4Ready
        : true;

  const { gate, clearReportGate, gateHeaderTarget, gateChatBlocked, firstGateTriggered } =
    useReportGate({
      enabledFeatures: enabledFeatureIds,
      reportGateDelayMs,
      reportIntervalMs: reportGateDelayMs,
      reportRefreshIntervalMs,
      sheet,
      hotspotsOn,
      f4DetailSeen,
      history,
      analysisErrors,
      isPreparationReady,
      refreshAnalysis,
      recordUiEvent,
    });
  // Report-feature ENTRY controls (the ChatHeader Mirror/Hotspots buttons,
  // RiskBar) stay hidden between report gates so the periodic checkpoint
  // is the only path to discovering them — but only for the report
  // experiment (reportIntervalMs > 0); every other path (researcher
  // preview, non-report arms) keeps the historical always-visible
  // behavior since there's no gate to hide behind. Visible while a gate
  // is actively up (the gate's target needs to render for the
  // participant to interact with it), and while browsing a Mirror/Meter
  // sheet the participant opened via the gate (so closing the sheet, not
  // the gate itself, hides the entry point again). Message tinting and
  // its legend are NOT gated by this: once hotspotsOn is true the tint
  // is the substance of F4, not a discovery affordance, and organic
  // engagement with already-tinted messages must keep working between
  // gate ticks (see the F4 organic-engagement coverage in
  // app-report-gate.test.jsx). `|| reportEngaged` covers the jump-away
  // case above: jumpToMessage clears `sheet` to reveal the flashed
  // message, but the participant hasn't dismissed the report — without
  // this they'd be stranded with no way back into Mirror/Meter until
  // the next periodic tick.
  const reportVisible =
    !(reportGateDelayMs > 0) ||
    ((firstGateTriggered ||
      gate !== null ||
      sheet === 'mirror' ||
      sheet === 'meter' ||
      reportEngaged) &&
      (firstGateTriggered || isPreparationReady));
  // comment in ChatHeader for why reportVisible alone would let the toggle
  // vanish again after the participant turns tint back off. Starts
  // wherever reportVisible/hotspotsOn already are on first render (covers
  // initialHotspotsOn=true arms and non-report paths) and never re-hides.
  const [hotspotsVisible, setHotspotsVisible] = useState(reportVisible || hotspotsOn);
  useEffect(() => {
    if (!hotspotsVisible && (reportVisible || hotspotsOn)) setHotspotsVisible(true);
  }, [hotspotsVisible, reportVisible, hotspotsOn]);
  const sessionStartedRef = useRef(false);
  useEffect(() => {
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;
    recordUiEvent(TelemetryEvent.SESSION_STARTED, {
      conversation_id: conversationId,
      enabled_feature_ids: enabledFeatureIds,
      f2_hint_timing: initialHintTiming,
      f2_agency_variant: initialF2Variant,
      telemetry_session_id: telemetry?.sessionId ?? null,
      build_version: appConfig.appVersion ?? 'unknown',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Session metadata is intentionally captured once at initialization.

  // Mid-experiment feature reveal — fire once, only for a genuinely
  // configured delay (revealDelayMs > 0). Arms with the default 0-minute
  // delay are "revealed" from first render (see isRevealed in App), which
  // is not a meaningful transition worth logging. Placed after the
  // session_started effect so recordUiEvent/conversationId are ready.
  const revealTelemetryFiredRef = useRef(false);
  useEffect(() => {
    if (!revealed || revealTelemetryFiredRef.current || !revealWasDelayed) return;
    revealTelemetryFiredRef.current = true;
    recordUiEvent(TelemetryEvent.FEATURE_REVEALED, {
      enabled_feature_ids: enabledFeatureIds,
      reveal_delay_ms: revealDelayMs,
      reveal_anchor_broken: isRevealAnchorBroken(enrolledAt, revealDelayMs),
    });

    // reveal transition; recordUiEvent/enabledFeatureIds/revealDelayMs are
    // intentionally read from the latest closure rather than re-triggering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  // Mid-experiment reveal backfill (M5): the instant the feature appears,
  // recompute its background analysis over the existing conversation so
  // Mirror (F1) / Risk (F3) have data immediately instead of empty state
  // until the participant's next send. One-shot, and only for a genuinely
  // delayed reveal — 0-delay arms are revealed from first render and their
  // analysis is driven by sends exactly as before. Skipped when a report
  // gate interval is configured (reportIntervalMs > 0): there the periodic
  // gate tick already owns every refreshAnalysis call (see useReportGate),
  // and it fires soon enough after reveal that a separate instant backfill
  // here would just double the call.
  const revealBackfilledRef = useRef(false);
  useEffect(() => {
    if (!revealed || revealBackfilledRef.current || !revealWasDelayed) return;
    if (reportGateDelayMs > 0) return;
    revealBackfilledRef.current = true;
    refreshAnalysis();

    // reveal transition; refreshAnalysis reads enabled features from its latest ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed]);

  const [sending, setSending] = useState(false);
  const sendPolicyContextRef = useRef({});
  sendPolicyContextRef.current = {
    f2Enabled,
    agency: f2DesignVariant,
    draftAssessment,
    latestMeterUpdate,
    latestMirror,
    send,
    previewDraft,
    scheduleReply,
    scoreDraft,
  };
  const sendPolicyUiEventRef = useRef(recordUiEvent);
  sendPolicyUiEventRef.current = recordUiEvent;
  const sendPolicyRef = useRef(null);
  if (sendPolicyRef.current === null) {
    sendPolicyRef.current = createSendPolicy({
      getMode: () => {
        const current = sendPolicyContextRef.current;
        return { f2Enabled: current.f2Enabled, agency: current.agency };
      },
      getDraftAssessment: () => sendPolicyContextRef.current.draftAssessment,
      getCommitContext: () => ({
        meterUpdate: sendPolicyContextRef.current.latestMeterUpdate,
        mirrorAggregate: sendPolicyContextRef.current.latestMirror,
      }),
      getLimitReached: () => limitReachedRef.current,
      send: (text) => sendPolicyContextRef.current.send(text),
      previewDraft: (text, options) => sendPolicyContextRef.current.previewDraft(text, options),
      onPendingChange: (next) => {
        setPendingPreview(next);
        setSheet(next ? 'hardstop' : null);
      },
      onCommit: () => {
        draftRevisionRef.current += 1;
        draftTextRef.current = '';
        setDraftText('');
      },
      onCancel: (textOverride) => {
        draftRevisionRef.current += 1;
        draftTextRef.current = textOverride;
        setDraftText(textOverride);
        sendPolicyContextRef.current.scoreDraft(textOverride, { trigger: 'hard-cancel' });
      },
      onBusyChange: setSending,
      onFailure: handleSendFailure,
      scheduleReply: () => sendPolicyContextRef.current.scheduleReply(),
      recordUiEvent: (...args) => sendPolicyUiEventRef.current(...args),
    });
  }
  const sendPolicy = sendPolicyRef.current;
  // Mock-mode seed bootstrap: when draftText is pre-filled with
  // MOCK_DEMO_DRAFT, fire scoreDraft once so the at-risk highlights
  // render the moment the demo opens (regardless of D2 timing — the
  // seed is a complete draft, so both levels would have scored it).
  // Without this, the spans would only appear after the first
  // keystroke (which would also mutate the seed). The hook's debounce
  // still applies, so the call settles ~250ms after mount.
  useEffect(() => {
    if (draftText) scoreDraft(draftText, { trigger: 'demo' });
    // Run-on-mount only; subsequent keystrokes go through onDraftTextChange.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!(timeLimitMs > 0)) {
      setRemainingMs(null);
      return undefined;
    }
    const tick = () => {
      const next = remainingTimeMs(enrolledAt, timeLimitMs);
      setRemainingMs(next);
      if (next === 0) {
        setSendError(null);
        setLimitReached((current) => current ?? 'time');
        sendPolicy.cancel(undefined, { silent: true });
        setSheet(null);
        return false;
      }
      return true;
    };
    if (!tick()) return undefined;
    const id = setInterval(() => {
      if (!tick()) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [enrolledAt, timeLimitMs, sendPolicy]);
  // Mid-experiment feature reveal timer. One-shot: `revealed` starts true
  // whenever revealDelayMs is 0 (isRevealed in App), so this effect is a
  // no-op for every arm except a report arm with a configured delay.
  useEffect(() => {
    if (revealed) return undefined;
    const delay = timeUntilRevealMs(enrolledAt, revealDelayMs);
    if (delay <= 0) {
      setRevealed(true);
      return undefined;
    }
    const id = setTimeout(() => setRevealed(true), delay);
    return () => clearTimeout(id);
    // `revealed`/setRevealed intentionally excluded: setRevealed is stable,
    // and re-checking `revealed` here only matters at the moment the timer
    // is (re)armed for a new enrolledAt/revealDelayMs pair, not on every
    // reveal flip this same effect instance causes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolledAt, revealDelayMs]);

  // T8: annotated-composer text change — where D2 (hint timing) lives.
  //   'word'     → scoreDraft fires whenever the draft now ends on a
  //                word boundary (whitespace), or after a quiet idle period
  //                so a final token without whitespace still gets scored.
  //   'sentence' → scoreDraft fires whenever the draft now ends on a
  //                sentence boundary (newline / . / ? / !), so hints
  //                refresh at sentence granularity instead of per word.
  // This checks the resulting text, not just newly-typed characters, so a
  // paste/replace that lands the draft on a boundary (e.g. selecting the
  // whole composer and pasting a shorter reply ending in a space) still
  // re-scores instead of leaving spans from the old, now-unrelated draft.
  // Clearing or blanking the composer cancels queued/in-flight scoring and
  // clears the local assessment. Empty text never enters the inference queue.
  // Edits that don't land on a boundary keep the previously revealed spans
  // on screen until the next D2 trigger.
  const clearDraftIdleTimer = () => {
    if (draftIdleTimerRef.current !== null) {
      clearTimeout(draftIdleTimerRef.current);
      draftIdleTimerRef.current = null;
    }
  };

  const onDraftTextChange = (t, { trigger } = {}) => {
    // Keep this ref in sync before React schedules the render so an
    // in-flight SAFER preview can reject a response for an older draft.
    draftTextRef.current = t;
    draftRevisionRef.current += 1;
    setDraftText(t);
    clearDraftIdleTimer();

    if (!t.trim()) {
      cancelDraftScoring();
      setTypingOpenIdx(null);
      return;
    }

    const boundaryRe = HINT_TIMING_BOUNDARY_RE[hintTiming];
    const endsAtBoundary = boundaryRe?.test(t.slice(-1));
    if (endsAtBoundary) {
      scoreDraft(t, { trigger: trigger ?? hintTiming });
      return;
    }

    if (hintTiming === 'word') {
      draftIdleTimerRef.current = setTimeout(() => {
        draftIdleTimerRef.current = null;
        scoreDraft(t, { trigger: trigger ?? 'idle' });
      }, F2_IDLE_TRIGGER_MS);
    }
  };

  // T2: Mirror reads latestMirror from the hook. Falls back to the seeded
  // MIRROR_DATA fixture before any send has populated the hook — keeps
  // first-load demo + live mode (?mock=0 before backend lands) both rendering.
  // Once the backend is wired and seeds the conversation, the fallback can go.
  const mirrorView = latestMirror ?? MIRROR_DATA;

  // Risk surfaces consume the canonical RiskAxis[] contract directly.
  // Before the first analysis, the canonical mock fixture keeps the demo
  // and live-mode initial state visible.
  const meterView = latestMeterUpdate.length > 0 ? latestMeterUpdate : data.meter;

  // Reveal-race guard: true only while the *first-ever* F1/F3 analysis
  // (the M5 backfill above) is in flight — latestMirror/latestMeterUpdate
  // become permanently non-null/non-empty after the first response, so
  // these can't re-trigger on later background refreshes and stay false
  // in zero-delay arms before any send. Consumers use this to show
  // "Analyzing…" instead of the misleading fixture fallback.
  const mirrorPending = latestMirror == null && analysisUpdating.F1;
  const meterPending = latestMeterUpdate.length === 0 && analysisUpdating.F3;

  const sentMessages = history;
  const chatScrollRef = useRef(null);
  const atBottomRef = useRef(true);

  const updateChatScrollPosition = () => {
    const el = chatScrollRef.current;
    if (!el) return;
    atBottomRef.current = isNearBottom(el);
  };

  useLayoutEffect(() => {
    const el = chatScrollRef.current;
    if (!el || !atBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [sentMessages.length, replyTyping]);
  const onSendLive = async (text) => {
    clearDraftIdleTimer();
    draftRevisionRef.current += 1;
    const submission = sendPolicy.submit(text);
    cancelDraftScoring();
    return submission;
  };

  // D4L1 Soft "Redact" — rewrite the draft in place with the §6.3
  // replacements. No popup: the participant sees the rewritten text
  // in the composer, can keep editing, and sends when ready. The
  // immediate re-score (bypassing the D2 timing gate — redact counts
  // as a deliberate act, like a sentence boundary) refreshes the
  // highlights so cleaned spans stop reading as risky.
  const onRedactDraft = async () => {
    const attemptId = `${conversationId}-redact-${++redactAttemptSeqRef.current}`;
    recordUiEvent(TelemetryEvent.REDACT_ATTEMPT, { attemptId });
    let scoreId;
    const finish = (outcome, detail = {}) =>
      recordUiEvent(TelemetryEvent.REDACT_OUTCOME, { attemptId, scoreId, outcome, ...detail });
    if (redactInFlightRef.current) {
      finish('no-op', { reason: 'busy' });
      return;
    }
    redactInFlightRef.current = true;
    clearDraftIdleTimer();
    cancelDraftScoring();
    const textAtClick = draftTextRef.current;
    const revisionAtClick = draftRevisionRef.current;
    const isCurrent = () =>
      draftRevisionRef.current === revisionAtClick && draftTextRef.current === textAtClick;
    try {
      if (!textAtClick.trim()) {
        finish('no-op', { reason: 'empty' });
        return;
      }

      let spans;
      if (isDraftAssessmentForText(draftAssessment, textAtClick)) {
        spans = draftAssessment.spans;
        scoreId = draftAssessment.scoreId;
      } else {
        let preview;
        try {
          preview = await previewDraft(textAtClick, {
            trigger: 'manual-preview',
            attemptId,
            isCurrent,
          });
        } catch (err) {
          finish('failed');
          console.error('[onRedactDraft] preview failed', err);
          return;
        }
        if (!isCurrent()) {
          finish('stale');
          return;
        }
        spans = preview?.spans;
      }

      if (!spans?.length) {
        finish('no-findings', { spanCount: 0 });
        return;
      }
      const redacted = applyReplacements(textAtClick, spans);
      if (redacted === textAtClick) {
        finish('no-op', { reason: 'unchanged', spanCount: spans.length });
        return;
      }
      draftRevisionRef.current += 1;
      draftTextRef.current = redacted;
      setDraftText(redacted);
      recordUiEvent(TelemetryEvent.REDACT_INVOKED, { attemptId, scoreId, spanCount: spans.length });
      finish('applied', { scoreId, spanCount: spans.length });
      scoreDraft(redacted, { trigger: 'post-redaction', attemptId });
    } finally {
      redactInFlightRef.current = false;
    }
  };

  const openMirror = () => {
    recordUiEvent(TelemetryEvent.F1_MIRROR_OPEN);
    setEntrySeen((prev) => (prev.F1 ? prev : { ...prev, F1: true }));
    setReportEngaged(true);
    // Report-arm analysis is refreshed by the periodic gate tick
    // instead (see useReportGate/gateTickRef) — calling it again here
    // would just double that call. Non-report paths (reportIntervalMs
    // === 0) have no tick, so this is their only refresh-on-open.
    if (!(reportGateDelayMs > 0)) refreshAnalysis();
    setSheet('mirror');
    // Opening the report is exactly what the f1 report gate asks for —
    // clear it so the composer/other controls unblock immediately.
    if (gate?.arm === 'F1') clearReportGate('open_mirror');
  };

  const closeMirror = () => {
    recordUiEvent(TelemetryEvent.F1_MIRROR_CLOSE);
    setReportEngaged(false);
    setSheet(null);
  };

  const expandRiskMeter = () => {
    recordUiEvent(TelemetryEvent.F3_METER_EXPAND);
    setEntrySeen((prev) => (prev.F3 ? prev : { ...prev, F3: true }));
    setReportEngaged(true);
    // See openMirror above: the periodic gate tick owns the refresh
    // for report arms, this is only needed on non-report paths.
    if (!(reportGateDelayMs > 0)) refreshAnalysis();
    setSheet('meter');
    // Opening the Risk Meter is exactly what the f3 report gate asks
    // for — clear it so the composer/other controls unblock immediately.
    if (gate?.arm === 'F3') clearReportGate('expand_risk_meter');
  };

  // F4 participant-facing tint toggle (ChatHeader's Hotspots pill) — the
  // participant equivalent of the researcher FeatureLauncher's "Tint
  // On/Off" Seg control, both driving the same hotspotsOn state.
  const toggleHotspotsParticipant = () => {
    const next = !hotspotsOn;
    setHotspotsOn(next);
    recordUiEvent(TelemetryEvent.F4_HOTSPOT_TOGGLE, { on: next });
    setEntrySeen((prev) => (prev.F4 ? prev : { ...prev, F4: true }));
    // Report-gate clear: turning hotspots ON while the gate's
    // 'enable' phase is active either satisfies the gate outright
    // (detail already seen, or nothing sent yet to tap) or advances it
    // to the one-time 'tap' phase — never both, so a later tick doesn't
    // re-trigger 'tap' after it's already been satisfied.
    if (next && gate?.arm === 'F4' && gate?.phase === 'enable') {
      const hasTappable = history.some((m) => m.role === 'me' && m.inferences?.length);
      clearReportGate(
        'enable_hotspots',
        !f4DetailSeen && hasTappable ? { arm: 'F4', phase: 'tap' } : null
      );
    }
  };

  const closeRiskMeter = () => {
    recordUiEvent(TelemetryEvent.F3_METER_CLOSE);
    setReportEngaged(false);
    setSheet(null);
  };

  const closeDrilldown = () => {
    recordUiEvent(TelemetryEvent.F3_DRILLDOWN_CLOSE, {
      axis: riskAxisLabel(drilldownAxis),
    });
    setReportEngaged(false);
    setSheet(null);
    setDrilldownAxis(null);
  };

  const backToMeter = () => {
    recordUiEvent(TelemetryEvent.F3_DRILLDOWN_CLOSE, {
      axis: riskAxisLabel(drilldownAxis),
    });
    setSheet('meter');
    setDrilldownAxis(null);
  };

  // Explicit Finish (App -> Qualtrics handoff) — never automatic, always a
  // deliberate participant action. requestFinish opens a confirm; confirmFinish
  // emits the completion telemetry event (forced through beacon — see
  // harness/telemetry.js) and calls onFinish, which ParticipantGate uses to
  // persist finishedAt and swap in the completion screen.
  const requestFinish = () => {
    recordUiEvent(TelemetryEvent.FINISH_REQUESTED, { limit_reached: limitReached });
    setFinishConfirmOpen(true);
  };
  const cancelFinish = () => {
    recordUiEvent(TelemetryEvent.FINISH_CANCELLED);
    setFinishConfirmOpen(false);
  };
  const confirmFinish = async () => {
    const reason = limitReached ?? 'manual';
    recordUiEvent(TelemetryEvent.STUDY_COMPLETE, {
      enabled_feature_ids: enabledFeatureIds,
      reason,
    });
    setFinishConfirmOpen(false);
    // Wait for the beacon-flushed study_complete row to actually leave before
    // handing off to CompleteScreen — the participant follows the Qualtrics
    // link from there and never returns to this tab.
    await telemetry?.flush?.();
    onFinish(reason);
  };

  const countdownText = remainingMs !== null ? formatCountdown(remainingMs) : null;
  const composerDisabled = sending || Boolean(limitReached) || Boolean(gate);

  return (
    <div className="app-bg">
      <div
        className="device-wrap"
        style={{
          width: 'min(390px, calc(100vw - 48px))',
          height: 'min(844px, calc(var(--app-viewport-height) - 48px))',
          position: 'relative',
          borderRadius: 48,
          background: 'var(--paper)',
          overflow: 'hidden',
          boxShadow: `
          0 40px 80px oklch(25% 0.02 260 / 0.18),
          0 0 0 1px oklch(25% 0.02 260 / 0.12),
          0 0 0 11px #141418,
          0 0 0 12px oklch(20% 0.005 80)
        `,
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 120,
            height: 34,
            background: '#000',
            borderRadius: 20,
            zIndex: 50,
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            background: 'var(--paper-2)',
          }}
        >
          <ChatHeader
            contact={data.contact}
            onOpenMirror={openMirror}
            countdownText={countdownText}
            showMirrorDot={revealWasDelayed && f1Enabled && !entrySeen.F1}
            hotspotsOn={hotspotsOn}
            showF4Dot={revealWasDelayed && f4Enabled && !entrySeen.F4}
            onToggleHotspots={toggleHotspotsParticipant}
            researcherMode={researcherMode}
            onRequestFinish={requestFinish}
            gateTarget={gateHeaderTarget}
            gateActive={Boolean(gate)}
            reportVisible={reportVisible}
            hotspotsVisible={hotspotsVisible}
          />

          {sendError && (
            <div
              role="alert"
              aria-live="polite"
              style={{
                padding: '8px 14px',
                background: 'oklch(95% 0.04 30)',
                borderTop: '1px solid oklch(72% 0.13 30)',
                borderBottom: '1px solid oklch(72% 0.13 30)',
                color: 'oklch(35% 0.13 30)',
                fontSize: 12,
                lineHeight: 1.4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
              }}
            >
              <span>Send failed: {sendError}</span>
              <button
                type="button"
                aria-label="Dismiss send error"
                onClick={() => setSendError(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                  padding: 4,
                  lineHeight: 1,
                  fontFamily: 'inherit',
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                ×
              </button>
            </div>
          )}

          {limitReached && (
            <div
              role="status"
              aria-live="polite"
              style={{
                padding: '10px 14px',
                background: 'var(--indigo-soft)',
                borderTop: '1px solid var(--line)',
                borderBottom: '1px solid var(--line)',
                color: 'var(--ink)',
                fontSize: 12,
                lineHeight: 1.45,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <span>
                {limitReached === 'time'
                  ? "You've reached the time limit. Tap Finish when you're ready to continue to the survey."
                  : "You've used this session's messages. You can still review the conversation and reports. Tap Finish to continue to the survey."}
              </span>
              <button
                type="button"
                onClick={requestFinish}
                style={{
                  background: 'var(--indigo)',
                  color: 'var(--paper)',
                  border: 'none',
                  borderRadius: 999,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 12,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                Finish
              </button>
            </div>
          )}

          {f3Enabled && reportVisible && (
            // Wrapped so the f3 report gate can elevate RiskBar above
            // the scrim (gateTargetStyle) — mutual exclusion (see
            // enabled-features.jsx) means RiskBar only ever coexists with
            // a null gate or the F3 gate, never F1/F4's.
            <div style={gateTargetStyle(gate?.arm === 'F3')}>
              <RiskBar
                className={gate?.arm === 'F3' ? 'report-gate-target' : undefined}
                meter={meterView}
                onExpand={expandRiskMeter}
                showDot={revealWasDelayed && !entrySeen.F3}
                unavailable={Boolean(analysisErrors.F3)}
                pending={meterPending}
                disabled={Boolean(gate && gate.arm !== 'F3')}
                inferenceTriggerTime={inferenceTriggerTimes.F3}
              />
            </div>
          )}

          <div
            data-chat-scroll
            ref={chatScrollRef}
            onScroll={updateChatScrollPosition}
            className="scroll"
            style={{
              flex: 1,
              overflow: 'auto',
              // Top padding bumped from 14 → 24 so the .msg-flash
              // box-shadow ring on the topmost message doesn't get
              // clipped by the scroll container's overflow:auto. See
              // SCROLL_HEADROOM_PX in use-message-jump.js.
              padding: '24px 14px 12px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              // Report-gate block: dimmed + inert while a gate is up,
              // except during F4's 'tap' phase, where the chat body IS
              // the gate's target (see the message-row wrap below).
              ...gateBlockedStyle(gateChatBlocked),
            }}
          >
            {sentMessages.map((msg) => {
              const view = withLegacyViewFields(msg);
              // String() on both sides — useMessageJump normalizes the
              // stored flashMsgId to string because evidence msgIds
              // come through as strings from the §6 adapters
              // (buildMirrorAggregate, adaptAxisEvidence) while
              // CHAT_DATA seeds carry numeric ids. Strict === would
              // mismatch and the flash class would never apply on
              // drilldown / live-mirror jumps.
              const flashing = flashMsgId === String(view.id);
              // Report-gate spotlight: during F4's one-time 'tap'
              // phase, every currently-tappable sent message is a valid
              // target (whichever one the participant taps first
              // satisfies the requirement — see onToggle below).
              const isTapTarget =
                gate?.arm === 'F4' &&
                gate?.phase === 'tap' &&
                view.from === 'me' &&
                view.inferences?.length > 0;
              return (
                <div
                  key={view.id}
                  ref={(el) => (msgRefs.current[view.id] = el)}
                  className={
                    [flashing ? 'msg-flash' : null, isTapTarget ? 'report-gate-target' : null]
                      .filter(Boolean)
                      .join(' ') || undefined
                  }
                  style={{
                    // scrollMarginTop is dead with manual scrollTo
                    // (see use-message-jump.js); keep ~24 in case a
                    // future change swaps in scrollIntoView, which
                    // does honor it.
                    scrollMarginTop: 24,
                    borderRadius: 18,
                    transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transform: flashing ? 'scale(1.06)' : 'scale(1)',
                    ...gateTargetStyle(isTapTarget),
                  }}
                >
                  <MessageBubble
                    msg={view}
                    expanded={expandedMsgId === view.id}
                    onToggle={() => {
                      const nextExpandedMsgId = expandedMsgId === view.id ? null : view.id;
                      const tappableF4Message =
                        f4Enabled && hotspotsOn && view.from === 'me' && view.inferences?.length;
                      if (f4Enabled) {
                        recordUiEvent(TelemetryEvent.F4_HOTSPOT_OPEN, {
                          msgId: view.id,
                          expanded: nextExpandedMsgId !== null,
                        });
                      }
                      setExpandedMsgId(nextExpandedMsgId);
                      // Any tappable message interaction is engagement,
                      // including closing a tooltip that was already open
                      // when a gate appeared. This also records organic F4
                      // engagement before a periodic gate is raised.
                      if (tappableF4Message) {
                        setF4DetailSeen(true);
                        if (gate?.arm === 'F4' && gate?.phase === 'tap') {
                          clearReportGate('tap_message');
                        }
                      }
                    }}
                    hotspotsOn={hotspotsOn && f4Enabled}
                    inferenceTriggerTime={inferenceTriggerTimes.F4}
                  />
                  {isTapTarget && (
                    <span
                      className="pm-notify-dot"
                      aria-hidden="true"
                      style={{ position: 'absolute', top: -3, right: -3 }}
                    />
                  )}
                </div>
              );
            })}

            {replyTyping && <TypingIndicator />}

            {f4Enabled && hotspotsOn && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  padding: '8px 4px 0',
                  fontFamily: 'IBM Plex Mono, monospace',
                  fontSize: 9,
                  color: 'var(--ink-muted)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                <span>hotspot risk</span>
                {inferenceTriggerTimes.F4 && (
                  <>
                    <span>·</span>
                    <InferenceFreshness
                      triggerTime={inferenceTriggerTimes.F4}
                      style={{ fontSize: 9 }}
                    />
                  </>
                )}
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span
                    style={{
                      width: 10,
                      height: 6,
                      background: 'var(--risk-1-bg)',
                      border: '1px solid var(--risk-1-edge)',
                      borderRadius: 2,
                    }}
                  />{' '}
                  low
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span
                    style={{
                      width: 10,
                      height: 6,
                      background: 'var(--risk-2-bg)',
                      border: '1px solid var(--risk-2-edge)',
                      borderRadius: 2,
                    }}
                  />{' '}
                  med
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span
                    style={{
                      width: 10,
                      height: 6,
                      background: 'var(--risk-3-bg)',
                      border: '1px solid var(--risk-3-edge)',
                      borderRadius: 2,
                    }}
                  />{' '}
                  high
                </span>
              </div>
            )}
          </div>

          {/* F2 owns the annotated composer (inline at-risk hints, both
              D2 timing levels). Without F2 the plain composer renders —
              the Phase-0 baseline surface with no privacy UI. */}
          {bannerVisible && <PrivacyBanner />}
          {f2Enabled ? (
            <AnnotatedComposer
              text={draftText}
              spans={draftAssessment?.spans ?? []}
              scoringStatus={
                draftScoring.text === draftText && draftText.trim() ? draftScoring.status : 'idle'
              }
              onTextChange={onDraftTextChange}
              onSend={onSendLive}

              onRedact={onRedactDraft}
              agency={f2DesignVariant}
              disabled={composerDisabled}
              openSpanIdx={typingOpenIdx}
              onSpanTap={(i) => {
                const expanded = typingOpenIdx !== i;
                setTypingOpenIdx(expanded ? i : null);
                recordUiEvent(TelemetryEvent.F2_SPAN_TOGGLE, { spanIndex: i, expanded });
              }}
              onSpanClose={() => {
                if (typingOpenIdx != null)
                  recordUiEvent(TelemetryEvent.F2_SPAN_CLOSE, { spanIndex: typingOpenIdx });
                setTypingOpenIdx(null);
              }}
            />
          ) : (
            <Composer
              value={draftText}
              onChange={setDraftText}
              onSend={onSendLive}
              disabled={composerDisabled}
            />
          )}

          <div
            style={{
              height: 28,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              paddingBottom: 8,
              background: 'var(--paper-2)',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 134,
                height: 5,
                background: 'var(--ink)',
                borderRadius: 99,
                opacity: 0.35,
              }}
            />
          </div>
        </div>

        <ReportGate gate={gate} />

        {finishConfirmOpen && (
          <FinishConfirmSheet onCancel={cancelFinish} onConfirm={confirmFinish} />
        )}

        {/* Sheets */}
        {sheet === 'meter' && (
          <RiskMeterSheet
            meter={meterView}
            onClose={closeRiskMeter}
            onOpenDrilldown={openDrilldownForAxis}
            unavailable={Boolean(analysisErrors.F3)}
            pending={meterPending}
            inferenceTriggerTime={inferenceTriggerTimes.F3}
          />
        )}
        {sheet === 'hardstop' && pendingPreview && (
          <HardStopSheet
            draft={{
              text: pendingPreview.text,
              spans: pendingPreview.spans ?? [{ t: pendingPreview.text }],
            }}
            draftRecord={{
              text: pendingPreview.text,
              inferences: pendingPreview.response.messageInferences,
            }}
            onReevaluateDraft={previewDraft}
            onModify={sendPolicy.cancel}
            onSend={sendPolicy.confirm}
            onAutoRedact={sendPolicy.hardStop.redactInvoked}
            onAckToggled={sendPolicy.hardStop.ackToggled}
            onRedactionSpanToggle={sendPolicy.hardStop.redactionSpanToggle}
            onRestoreAll={sendPolicy.hardStop.restoreAll}
            sendDisabled={Boolean(limitReached)}
          />
        )}
        {sheet === 'drilldown' && (
          <MeterDrilldownSheet
            meter={meterView}
            onClose={closeDrilldown}
            onBack={backToMeter}
            onJumpToMessage={jumpToMessage}
            axisEvidence={axisEvidence}
            analysisUpdating={analysisUpdating.F3}
            initialAxis={drilldownAxis}
            onAxisSelect={(axis) =>
              recordUiEvent(TelemetryEvent.F3_AXIS_SELECTED, { axis: riskAxisLabel(axis) })
            }
            onEvidenceJump={(axis, msgId) =>
              recordUiEvent(TelemetryEvent.F3_EVIDENCE_JUMP, {
                axis: riskAxisLabel(axis),
                msgId,
              })
            }
          />
        )}
        {sheet === 'mirror' && (
          <MirrorScreen
            mirror={mirrorView}
            meter={meterView}
            onClose={closeMirror}
            onJumpToMessage={jumpToMessage}
            onOpenDrilldown={f3Enabled ? openDrilldownForAxis : undefined}
            unavailable={Boolean(analysisErrors.F1)}
            mirrorPending={mirrorPending}
            meterPending={meterPending}
            meterUnavailable={latestMeterUpdate.length === 0 && Boolean(analysisErrors.F3)}
            inferenceTriggerTime={inferenceTriggerTimes.F1}
            onCategorySelect={(category) =>
              recordUiEvent(TelemetryEvent.F1_CATEGORY_SELECTED, { category })
            }
            onEvidenceJump={(category, msgId) =>
              recordUiEvent(TelemetryEvent.F1_EVIDENCE_JUMP, { category, msgId })
            }
          />
        )}
      </div>

      {/* Side nav: feature launcher for research demo (gated) */}
      {researcherMode && (
        <FeatureLauncher
          current={sheet}
          hotspotsOn={hotspotsOn}
          onSetSheet={setSheet}
          onToggleHotspots={() => setHotspotsOn((v) => !v)}
          hintTiming={hintTiming}
          setHintTiming={setHintTiming}
          setEnabledFeatures={setEnabledFeatures}
          bannerVisible={bannerVisible}
          setBannerVisible={setBannerVisible}
          f2DesignVariant={f2DesignVariant}
          setF2DesignVariant={setF2DesignVariant}
          onActivateF2={() => {
            // F2 entry seed: when the launcher activates an F2 row in
            // mock mode, refresh the composer to MOCK_DEMO_DRAFT so the
            // demo path is one-tap from "F2" to "Send and see preview".
            // No-op in live mode (?mock=0) so a researcher driving the
            // backend does not have their typed draft overwritten.
            if (typeof window === 'undefined') return;
            if (!isMockSeedActive(window.location.search, appConfig)) return;
            draftRevisionRef.current += 1;
            draftTextRef.current = MOCK_DEMO_DRAFT;
            setDraftText(MOCK_DEMO_DRAFT);
            scoreDraft(MOCK_DEMO_DRAFT, { trigger: 'demo' });
          }}
        />
      )}
    </div>
  );
}

// Feature launcher — a floating panel beside the device for researchers.
// Each F-row owns the experimental axes that modify *that* feature, so the
// study's design space (D1-D5) reads as nested under the feature it varies
// instead of as a flat sibling list. F1 has no axes; F2 owns D2 hint
// timing and D4 agency — two orthogonal segments, any combination valid;
// F3 owns the axis drill-down view; F4 owns the hotspot tint toggle.
function FeatureLauncher({
  current,
  hotspotsOn,
  onSetSheet,
  onToggleHotspots,
  hintTiming,
  setHintTiming,
  setEnabledFeatures,
  bannerVisible,
  setBannerVisible,
  f2DesignVariant,
  setF2DesignVariant,
  onSeedComposer,
}) {
  const enabled = useEnabledFeatures();

  // Mutual-exclusion invariant: every row writes setEnabledFeatures(['F#'])
  // so {F1, F3, F4} chat UI stays mutually exclusive (only one persistent
  // inline UI ever renders). F2 swaps the composer for the annotated one
  // and clears persistent UI behind it. Active-row = "this F# is the
  // currently enabled one."
  const f1Active = enabled.includes('F1');
  const f2Active = enabled.includes('F2');
  const f3Active = enabled.includes('F3');
  const f4Active = enabled.includes('F4');

  // F2 sub-axis state, derived from app state so the segments stay in
  // sync even if another path mutates them. D2 and D4 are orthogonal:
  // either timing combines with either agency level.
  const d2 = hintTiming;
  const d4 = f2DesignVariant === 'hard' ? 'hard' : 'soft';

  // F1 — mode switch. Mirror button appears in the chat header; the
  // researcher taps it to open the sheet, mirroring the participant
  // flow. No sheet auto-opens here.
  const goF1 = () => {
    setEnabledFeatures(['F1']);
    setBannerVisible(false);
    onSetSheet(null);
  };
  // F3 — mode switch. RiskBar appears in the chat; the researcher
  // taps it to open the meter sheet. Same shape as F1. The drill-down
  // sub-link additionally opens the drill-down sheet on top of the bar.
  const goF3 = () => {
    setEnabledFeatures(['F3']);
    setBannerVisible(false);
    onSetSheet(null);
  };
  const goF3Drilldown = () => {
    setEnabledFeatures(['F3']);
    setBannerVisible(false);
    onSetSheet('drilldown');
  };
  // F4 — mode switch. Hotspot tints + tap-to-reveal become active in
  // the chat. Without enabling F4 here, the bubble's hotspotsOn prop is
  // false (gated by f4Enabled in AppShell), so taps did nothing.
  const goF4 = () => {
    setEnabledFeatures(['F4']);
    setBannerVisible(false);
    onSetSheet(null);
  };
  // F2 — composer switch (annotated composer, no persistent chat UI
  // beyond it). Each F2 tap also writes ['F2'] so F1/F3/F4 are
  // *cleared* from the chat — preserving mutual exclusion. The two
  // segments below mutate their own axis and leave the other alone,
  // so any D2 × D4 combination is reachable. The composer is seeded
  // (mock mode) so the demo path is one tap from "F2" to "type/send".
  const goF2 = () => {
    setEnabledFeatures(['F2']);
    setBannerVisible(false);
    onSetSheet(null);
    onSeedComposer?.();
  };
  const goControl = () => {
    setEnabledFeatures([]);
    setBannerVisible(false);
    onSetSheet(null);
  };
  const goBanner = () => {
    setEnabledFeatures([]);
    setBannerVisible(true);
    onSetSheet(null);
  };
  const setD2 = (timing) => {
    goF2();
    setHintTiming?.(timing);
  };
  const setD4 = (agency) => {
    goF2();
    setF2DesignVariant?.(agency);
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: 'calc(50% + 220px)',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 280,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <div>
        <div className="eyebrow" style={{ marginBottom: 2 }}>
          Privacy Meter
        </div>
        <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: -0.2 }}>Research prototype</div>
      </div>

      <div
        style={{
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        <SectionLabel label="Experiment A · Post-reporting" first />
        <FeatureRow
          active={enabled.length === 0 && !bannerVisible}
          label="Control"
          desc="Plain chat baseline."
          onActivate={goControl}
        />

        <FeatureRow
          active={f1Active}
          label="F1 · Mirror"
          desc="Session-wide privacy receipt."
          onActivate={goF1}
        />

        <FeatureRow
          active={f3Active}
          label="F3 · Risk Meter"
          desc="Dual radar — you vs them."
          onActivate={goF3}
        >
          <SubLink
            label="Open axis drill-down"
            active={current === 'drilldown'}
            onClick={goF3Drilldown}
          />
        </FeatureRow>

        <FeatureRow
          active={f4Active}
          label="F4 · Inference Hotspot"
          desc="Per-message tint. Tap to reveal."
          onActivate={goF4}
        >
          <SubControl label="Tint">
            <Seg
              options={[
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
              ]}
              value={hotspotsOn ? 'on' : 'off'}
              onChange={(v) => {
                const want = v === 'on';
                if (want !== hotspotsOn) onToggleHotspots();
              }}
            />
          </SubControl>
        </FeatureRow>
      </div>

      <div
        style={{
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        <SectionLabel label="Experiment B · Pre-intervention" first />
        <FeatureRow
          active={bannerVisible}
          label="Banner"
          desc="Static placebo warning."
          onActivate={goBanner}
        />

        <FeatureRow
          active={f2Active}
          label="F2 · What-If"
          desc="D2 hint timing × D4 agency."
          onActivate={goF2}
        >
          <SubControl label="D2 hint timing">
            <Seg
              options={[
                { value: 'word', label: 'Word' },
                { value: 'sentence', label: 'Sentence' },
              ]}
              value={d2}
              onChange={setD2}
            />
          </SubControl>
          <SubControl label="D4 agency">
            <Seg
              options={[
                { value: 'soft', label: 'Soft nudge' },
                { value: 'hard', label: 'Hard stop' },
              ]}
              value={d4}
              onChange={setD4}
            />
          </SubControl>
        </FeatureRow>
      </div>
    </div>
  );
}

function SectionLabel({ label, first = false }) {
  return (
    <div
      className="mono"
      style={{
        padding: '9px 13px',
        borderTop: first ? 'none' : '1px solid var(--line)',
        background: 'var(--ink)',
        color: 'var(--paper)',
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      }}
    >
      {label}
    </div>
  );
}

function FeatureRow({ first, active, label, desc, onActivate, children }) {
  return (
    <div
      style={{
        borderTop: first ? 'none' : '1px solid var(--line)',
        background: active ? 'var(--indigo-soft)' : 'transparent',
      }}
    >
      <button
        onClick={onActivate}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '11px 13px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 99,
            background: active ? 'var(--indigo)' : 'var(--ink-faint)',
            flexShrink: 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: active ? 'var(--indigo)' : 'var(--ink)',
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-muted)', marginTop: 1 }}>{desc}</div>
        </div>
      </button>
      {children && (
        <div
          style={{
            padding: '2px 13px 11px 27px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function SubControl({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <span
        className="mono"
        style={{
          fontSize: 9,
          color: 'var(--ink-muted)',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      {children}
    </div>
  );
}

function Seg({ options, value, onChange, disabled }) {
  return (
    <div
      className="seg"
      style={{ opacity: disabled ? 0.45 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
      aria-disabled={disabled || undefined}
    >
      {options.map((o) => (
        <button
          key={o.value}
          className={value === o.value ? 'active' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SubLink({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: active ? 'var(--ink)' : 'transparent',
        color: active ? 'var(--paper)' : 'var(--ink)',
        border: '1px solid var(--line)',
        borderRadius: 8,
        padding: '6px 10px',
        fontFamily: 'IBM Plex Mono, monospace',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        cursor: 'pointer',
        alignSelf: 'flex-start',
      }}
    >
      {label}
    </button>
  );
}
